// Per-activity guest age sub-labels for the Adults / Children / Infants rows.
//
// The rule itself lives in `@majourneys/shared` (`ageBracketLabels`) so the
// website, the city sites and the app cannot disagree about who counts as an
// adult — they did, and a 13-year-old was priced two different ways depending
// on where the booking started. This file is only the rendering half.
//
// Every activity now renders from its own bounds, falling back per-field to the
// site-wide defaults. The previous version required BOTH infantMaxAge and
// childrenMaxAge before it used either, so Alkamar Camp Pool — which sets only
// `children_max_age = 12` — advertised the site-wide bands instead of its own.

import { ageBracketFallback, ageBracketLabels, type AgeBounds } from "@majourneys/shared";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type { AgeBounds };

export interface AgeSubLabels {
  adults: string;
  children: string;
  infants: string;
}

export function ageSubLabels(t: TranslateFn, bounds: AgeBounds): AgeSubLabels {
  const labels = ageBracketLabels(bounds);
  const render = (label: { key: string; params: Record<string, number> }) =>
    t(label.key, { ...label.params, defaultValue: ageBracketFallback(label) });

  return {
    adults: render(labels.adults),
    children: render(labels.children),
    infants: render(labels.infants),
  };
}

/**
 * A restaurant has no infant row, so its Children row covers everyone under
 * the adult age, babies included (they still need a seat or a high chair).
 */
export function tableChildrenSubLabel(t: TranslateFn, bounds: AgeBounds): string {
  const age = ageBracketLabels(bounds).adults.params.min;
  return t("booking_sheet.table_children_sub", {
    age,
    defaultValue: `Under ${age}, babies included`,
  });
}
